import { Button, ButtonProps } from "@/components/ui/button";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  AnimatePresence,
  motion,
  MotionValue,
  useScroll,
  useTransform,
} from "framer-motion";
import { ArrowRight } from "lucide-react";

import { useRef } from "react";
import { Link } from "react-router-dom";
import ImageSvg from "../ui/image-svg";

type ImageProps = {
  src: string;
  alt?: string;
};

type ContentProps = {
  heading: string;
  description: string;
  color: string;
  image: ImageProps;
  imageMobile: ImageProps;
};

type Props = {
  tagline: string;
  heading: string;
  description: string;
  buttons: ButtonProps[];
  data: ContentProps[];
};

export type WhoSectionProps = React.ComponentPropsWithoutRef<"section"> &
  Partial<Props>;

type WhoItemContentProps = {
  item: ContentProps;
  index: number;
  dataLength: number;
  scrollYProgress: MotionValue<number>;
  isMobile: boolean;
};

const WhoItemContent = ({
  item,
  index,
  dataLength,
  scrollYProgress,
  isMobile,
}: WhoItemContentProps) => {
  const startProgress = index / dataLength;
  const endProgress = (index + 1) / dataLength;
  const opacity = useTransform(
    scrollYProgress,
    [
      Math.max(0, startProgress - 0.07),
      startProgress,
      endProgress - 0.07,
      Math.min(1, endProgress),
    ],
    [0, 1, 1, 0],
  );
  const y = useTransform(
    scrollYProgress,
    [
      Math.max(0, startProgress - 0.1),
      startProgress,
      endProgress - 0.1,
      Math.min(1, endProgress),
    ],
    [100, 0, 0, -100],
  );
  const motionStyles = {
    opacity: opacity,
    y: y,
  };

  if (isMobile) {
    return (
      <div>
        <h3 className="mb-3 text-xl font-bold text-n-text-beige">
          {item.heading}
        </h3>
        <p className="text-n-text-light">{item.description}</p>
        <div className="mt-4">
          <ImageSvg
            src={item.imageMobile.src}
            alt={item.imageMobile.alt}
            className="w-full"
          />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      style={motionStyles}
      initial={index === 0 ? { opacity: 0, y: 100 } : false}
      animate={index === 0 ? { opacity: 1, y: 0 } : {}}
      transition={index === 0 ? { duration: 0.5 } : {}}
      className={`md:absolute first:md:relative p-5 rounded-xl ${item.color}`}
    >
      <h3 className="font-semibold md:mb-4 md:text-3xl text-n-text-green">
        {item.heading}
      </h3>
      <p className=" text-n-text-green">{item.description}</p>
    </motion.div>
  );
};

export const WhoSection = (props: WhoSectionProps) => {
  const { data, tagline, heading, description, buttons } = {
    ...WhoSectionDefaults,
    ...props,
  };
  const isMobile = useMediaQuery("(max-width: 767px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  return (
    <section
      ref={containerRef}
      id="para-quem"
      className=" py-10 md:py-24 bg-n-bg-green"
    >
      <div className="container">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start md:gap-20">
          <div className="flex flex-col gap-y-16 md:sticky md:top-20 md:mt-20 md:h-[calc(100vh_-10rem)] md:justify-center">
            <div className="flex flex-col">
              <p className="mb-3 font-semibold md:mb-4">{tagline}</p>
              <h2
                className="mb-5 text-3xl md:mb-6 md:text-4xl lg:text-5xl text-n-text-beige"
                style={{
                  fontFamily: "'Anton', sans-serif",
                  lineHeight: "1.2em",
                }}
              >
                {heading}
              </h2>
              <p className="md:text-md text-n-text-light font-light">
                {description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 md:mt-8">
                {buttons.map((button, buttonIndex) => (
                  <Link to="/fazer-cotacao" key={buttonIndex}>
                    <Button {...button}>
                      {button.title}
                      <ArrowRight className="ml-2 " />
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex flex-col justify-start gap-y-8">
              <AnimatePresence>
                {data.map((item, index) => (
                  <WhoItemContent
                    key={index}
                    item={item}
                    index={index}
                    dataLength={data.length}
                    scrollYProgress={scrollYProgress}
                    isMobile={isMobile}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
          <div className="hidden md:grid md:grid-cols-1 md:gap-4">
            {data.map((item, index) => (
              <div key={index} className="h-screen overflow-hidden md:mt-40">
                <ImageSvg
                  src={item.image.src}
                  alt={item.image.alt}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const WhoSectionDefaults: Props = {
  tagline: "",
  heading:
    "Especialistas em transporte de carros com soluções pensadas para você e seu negócio.",
  description:
    "Pessoas e empresas que nos procuram geralmente possuem algumas destas necessidades:",
  buttons: [
    { title: "Fazer cotação", variant: "default", id: "btn-fazer-cotacao" },
  ],
  data: [
    {
      heading: "Vai mudar de cidade ou de emprego?",
      description:
        "Levar o veículo junto com a mudança é mais do que comodidade: é preservar seu estilo de vida no novo destino. A Nacional GO cuida de todo o processo, garantindo que seu carro ou moto chegue em perfeito estado, no mesmo prazo da sua mudança.",
      color: "bg-n-green-s-light",
      image: {
        src: "mudanca.svg",
        alt: "Vai mudar de cidade ou de emprego?",
      },
      imageMobile: {
        src: "mudanca.svg",
        alt: "Vai mudar de cidade ou de emprego?",
      },
    },
    {
      heading: "Compra ou venda particular",
      description:
        "Quando a negociação é feita entre particulares, o transporte seguro do veículo é essencial para concluir o negócio com tranquilidade. A Nacional GO garante retirada rápida, entrega pontual e toda a segurança necessária para que a experiência de compra ou venda seja positiva para ambas as partes.",
      color: "bg-n-beige",
      image: {
        src: "negociar.svg",
        alt: "Compra ou venda particular",
      },
      imageMobile: {
        src: "negociar.svg",
        alt: "Compra ou venda particular",
      },
    },
    {
      heading: "Leilão",
      description:
        "Veículos arrematados em leilões exigem logística ágil e especializada. Nossa equipe está preparada para realizar a retirada no pátio do leiloeiro e garantir a entrega do veículo com segurança até o endereço do cliente. Tudo isso com suporte completo na documentação e nos cuidados exigidos por esse tipo de operação.",
      color: "bg-n-green-h-light",
      image: {
        src: "leilao.svg",
        alt: "Leilão",
      },
      imageMobile: {
        src: "leilao.svg",
        alt: "Leilão",
      },
    },
    {
      heading: "Turismo",
      description:
        "Quem viaja de férias ou a trabalho pode contar com a comodidade de ter seu próprio veículo no destino. A Nacional GO oferece soluções práticas para transportar carros e motos, garantindo que estejam prontos para o uso assim que você chegar. Uma forma de viajar com mais liberdade e conforto.",
      color: "bg-n-green-s-light",
      image: {
        src: "turismo.svg",
        alt: "Turismo",
      },
      imageMobile: {
        src: "turismo.svg",
        alt: "Turismo",
      },
    },
    {
      heading: "Colecionador",
      description:
        "Carros antigos e veículos de coleção exigem atenção redobrada. Por isso, criamos protocolos de transporte que preservam a originalidade e a integridade de cada detalhe. Utilizamos logística diferenciada e seguro específico, garantindo tranquilidade para apaixonados por automóveis clássicos.",
      color: "bg-n-text-beige",
      image: {
        src: "colecionadores.svg",
        alt: "Colecionador",
      },
      imageMobile: {
        src: "colecionadores.svg",
        alt: "Colecionador",
      },
    },
    {
      heading: "Militar ou servidor público",
      description:
        "Transferências de cidade ou estado fazem parte da rotina de militares e servidores. Pensando nisso, a Nacional GO oferece condições diferenciadas e uma logística adaptada a esse público, garantindo que o veículo acompanhe cada nova etapa com segurança e pontualidade.",
      color: "bg-n-green-h-light",
      image: {
        src: "militar.svg",
        alt: "Sua operação não pode parar — e, com a gente, não para!",
      },
      imageMobile: {
        src: "militar.svg",
        alt: "Sua operação não pode parar — e, com a gente, não para!",
      },
    },
  ],
};
